import * as echarts from 'echarts';
import React, { useEffect, useRef } from 'react';
import { DASHBOARD_COLORS } from '../../../../../../../constants/DashboardColors';

interface ChartItem {
  label: string;
  value: number;
}

interface PieChartProps {
  data: ChartItem[];
  title?: string;
  colors?: string[];
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  title = 'Invitation Status',
  colors = DASHBOARD_COLORS,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const filteredData = data.filter((item) => item.value > 0);

  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) return;
    const myChart = echarts.init(chartRef.current);

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        textStyle: { color: '#1e293b' },
        borderWidth: 0,
        extraCssText:
          'box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); border-radius: 8px;',
      },
      graphic: {
        type: 'text',
        left: 'center',
        top: 'center',
        style: {
          text: totalValue.toString(),
          fill: '#1e293b',
          fontSize: 22,
          fontWeight: 'bold',
        },
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: ['65%', '85%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          emphasis: { label: { show: false } },
          data: filteredData.map((item, index) => ({
            value: item.value,
            name: item.label,
            itemStyle: {
              color: colors[index % colors.length],
            },
          })),
        },
      ],
    };

    myChart.setOption(option);

    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [filteredData, title, colors, totalValue]);

  return (
    <div className='flex h-full min-h-[160px] w-full flex-col rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-all hover:shadow-md sm:min-h-[190px] lg:min-h-[220px]'>
      <div className='mb-2 flex shrink-0 items-center justify-between'>
        <h2 className='text-sm font-semibold tracking-tight text-slate-900'>
          { title }
        </h2>
      </div>

      { totalValue > 0 ? (
        <div className='flex min-h-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center'>
          <div className='relative h-36 w-full sm:h-full sm:w-[58%]'>
            <div ref={chartRef} className='w-full h-full' />
          </div>

          <div className='flex max-h-full w-full flex-col justify-center gap-1 overflow-y-auto pr-1 sm:w-[42%]'>
            { filteredData.map((entry, index) => (
              <div
                  key={index}
                  className='group flex items-center justify-between py-0.5'
              >
                <div className='flex max-w-[75%] items-center gap-2'>
                  <span
                      className='h-2 w-2 rounded-full shrink-0'
                      style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  <span className='truncate text-[11px] font-semibold text-slate-600 transition-colors group-hover:text-slate-900'>
                    { entry.label }
                  </span>
                </div>
                <span className='shrink-0 text-xs font-bold text-slate-800'>
                  { entry.value }
                </span>
              </div>
            )) }
          </div>
        </div>
      ) : (
        <div className='flex min-h-[92px] flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-3 text-center'>
          <div>
            <p className='text-xs font-semibold text-slate-600'>No data yet</p>
            <p className='mt-0.5 text-[11px] text-slate-400'>
              Metrics will appear after interviews are available.
            </p>
          </div>
        </div>
      ) }
    </div>
  );
};

export default PieChart;
